import { formatDial } from "../engine/format";
import { roundToDial, type Grinder } from "../engine/model";
import { parseNumber } from "./format";

/**
 * Grind display and input for every dial format and direction. The engine only ever sees numbers, always with
 * "higher = coarser"; this is the app's view of them.
 *
 * macro_micro (Baratza Sette): x = macro + letterIndex/9 with letters A (0, finest) … I (8, coarsest). Formatting
 * snaps to the nearest 1/9 first, so rounding carries naturally: 9 + 9/9 → "10A".
 *
 * higher_finer grinders are mirrored: the stored setting is x = min + max − dial, so what the user reads and types is
 * the dial number and the engine still sees a coarser-is-higher axis.
 */
export const MICRO_LETTERS = "ABCDEFGHI";
const STEPS_PER_MACRO = MICRO_LETTERS.length; // 9

export function isMacroMicro(g: Grinder | null): boolean {
  return g?.dialFormat === "macro_micro";
}

export function isMirrored(g: Grinder | null): boolean {
  return g?.dialDirection === "higher_finer";
}

/** Stored setting → number on the dial (and back: the mirror is its own inverse). */
export function toDial(x: number, g: Grinder): number {
  return isMirrored(g) ? g.minGrind + g.maxGrind - x : x;
}
export const fromDial = toDial;

/** Snap a stored setting to the grinder's grid (1/9 for macro/micro, the dial resolution otherwise). */
export function snapGrind(x: number, g: Grinder): number {
  const d = toDial(x, g);
  const snapped = isMacroMicro(g) ? Math.round(d * STEPS_PER_MACRO) / STEPS_PER_MACRO : roundToDial(g, d);
  return isMirrored(g) ? Math.round(fromDial(snapped, g) * 1e9) / 1e9 : snapped;
}

/** "9E" for macro/micro grinders, "7.4" for decimal ones; always the number printed on the dial. */
export function formatGrind(x: number, g: Grinder): string {
  const d = toDial(x, g);
  if (!isMacroMicro(g)) return formatDial(d, g);
  const n = Math.round(d * STEPS_PER_MACRO);
  const macro = Math.floor(n / STEPS_PER_MACRO);
  const letter = n - macro * STEPS_PER_MACRO;
  return `${macro}${MICRO_LETTERS[letter]}`;
}

/**
 * Parse what the user typed into a stored setting. Macro/micro grinders accept "9E", "9e", "9 E" or a decimal such as
 * 9.44; letters outside A–I are rejected (null). Decimal grinders accept numbers only (comma or point).
 */
export function parseGrind(text: string | null | undefined, g: Grinder | null): number | null {
  if (text === null || text === undefined) return null;
  const t = text.trim();
  let d: number | null;
  if (isMacroMicro(g)) {
    const m = /^(\d+)\s*([a-z])$/i.exec(t);
    if (m) {
      const idx = MICRO_LETTERS.indexOf((m[2] as string).toUpperCase());
      d = idx < 0 ? null : Number(m[1]) + idx / STEPS_PER_MACRO;
    } else {
      d = /[a-z]/i.test(t) ? null : parseNumber(t);
    }
  } else {
    d = parseNumber(t);
  }
  return d === null || g === null ? d : fromDial(d, g);
}

/** Which way the stored setting moves when the user presses + (the dial number goes up). */
export function plusDirection(g: Grinder | null): 1 | -1 {
  return isMirrored(g) ? -1 : 1;
}

/** "± 2 letters" for macro/micro (whole letters, at least 1), "± 0.3" otherwise; "" when unknown. */
export function formatUncertainty(u: number, g: Grinder): string {
  if (!Number.isFinite(u)) return "";
  if (!isMacroMicro(g)) return `± ${u.toFixed(1)}`;
  const letters = Math.max(1, Math.round(u * STEPS_PER_MACRO));
  return `± ${letters} ${letters === 1 ? "letter" : "letters"}`;
}

/**
 * Chart x ticks (stored settings, ascending): one per macro step for macro/micro grinders (thinned if crowded); when
 * fewer than two macro steps are in view, ticks fall on whole letters instead. Five even ticks for decimal grinders.
 */
export function grindTicks(minX: number, maxX: number, g: Grinder): number[] {
  if (isMacroMicro(g)) {
    const lo0 = Math.min(toDial(minX, g), toDial(maxX, g)), hi0 = Math.max(toDial(minX, g), toDial(maxX, g));
    let ticks: number[] = [];
    for (let m = Math.ceil(lo0 - 1e-9); m <= hi0 + 1e-9; m++) ticks.push(m);
    while (ticks.length > 6) ticks = ticks.filter((_, i) => i % 2 === 0);
    if (ticks.length < 2) {
      const lo = Math.ceil(lo0 * STEPS_PER_MACRO - 1e-9), hi = Math.floor(hi0 * STEPS_PER_MACRO + 1e-9);
      const every = Math.max(1, Math.ceil((hi - lo) / 5));
      ticks = [];
      for (let n = lo; n <= hi; n += every) ticks.push(n / STEPS_PER_MACRO);
    }
    if (ticks.length >= 2) return ticks.map((d) => fromDial(d, g)).sort((a, b) => a - b);
  }
  return Array.from({ length: 5 }, (_, i) => minX + ((maxX - minX) * i) / 4);
}

/** Error text for an unparsable grind entry. */
export function grindInputError(g: Grinder | null): string {
  return isMacroMicro(g) ? "Grind setting must look like 9E (letters A–I) or a number" : "Grind setting must be a number";
}
